import React, { memo } from 'react';

import { Box, Typography } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { LoanEditorCustomFormSectionKind } from 'constants/loanEditorCustomForms';

import { CLOSING_FORM_PATH, DRAWER_MESSAGES } from '../../LoanEditorCustomFormDrawer.constants';
import {
  containerStyles,
  descriptionStyles,
  emptyStateStyles,
} from '../../LoanEditorCustomFormDrawer.styles';
import { LoanEditorCustomFormDrawerTestIds } from '../../LoanEditorCustomFormDrawer.testIds';
import LoanEditorCustomFormDrawerSkeleton from '../../LoanEditorCustomFormDrawerSkeleton';
import BorrowerSelector from '../BorrowerSelector';
import ClosingCompanySelector from '../ClosingCompanySelector';
import ClosingDateCascade from '../ClosingDateCascade';
import ContactSelector from '../ContactSelector';
import DrawerFieldsSection from '../DrawerFieldsSection';
import IframeSection from '../IframeSection';
import UnsupportedItemsNotice from '../UnsupportedItemsNotice';

const EntityOptionPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  fullName: PropTypes.string,
});

const DrawerContent = ({
  contentRef,
  isLoading,
  isSaving,
  form = null,
  sectionResolutions,
  unsupportedEntries,
  inapplicableEntries,
  hasBorrowerFields,
  borrowerSelectorOptions,
  selectedBorrowerId = null,
  onSelectBorrower,
  hasContactFields,
  contactSelectorOptions,
  selectedContactId = null,
  onSelectContact,
  hasClosingCompanyFields,
  closingCompanySelectorOptions,
  selectedClosingCompanyPrefix = null,
  onSelectClosingCompany,
  closingCascadeProps,
  iframeTokenContext,
  customFieldsController,
  formState,
  mergedPendingChanges,
  handleChange,
  setInForm,
  staticValidateField,
  validateField,
  validationErrors = null,
}) => {
  if (isLoading) {
    return <LoanEditorCustomFormDrawerSkeleton />;
  }

  if (!form || !form.sections?.length) {
    return (
      <Box sx={emptyStateStyles}>
        <Typography variant="regular14">
          {form ? DRAWER_MESSAGES.NO_SECTIONS : DRAWER_MESSAGES.FORM_NOT_FOUND}
        </Typography>
      </Box>
    );
  }

  const isDisabled = isSaving || isLoading;

  return (
    <Box ref={contentRef} sx={containerStyles} tabIndex={-1}>
      {form.description && (
        <Typography color="textSecondary" sx={descriptionStyles} variant="regular12">
          {form.description}
        </Typography>
      )}

      <UnsupportedItemsNotice entries={unsupportedEntries} />

      <UnsupportedItemsNotice
        dataTestId={LoanEditorCustomFormDrawerTestIds.INAPPLICABLE_NOTICE}
        entries={inapplicableEntries}
        message={DRAWER_MESSAGES.INAPPLICABLE_FIELDS_NOTICE}
      />

      {hasBorrowerFields && (
        <BorrowerSelector
          borrowers={borrowerSelectorOptions}
          disabled={isDisabled}
          selectedBorrowerId={selectedBorrowerId}
          onSelect={onSelectBorrower}
        />
      )}

      {hasContactFields && (
        <ContactSelector
          contacts={contactSelectorOptions}
          disabled={isDisabled}
          selectedContactId={selectedContactId}
          onSelect={onSelectContact}
        />
      )}

      {hasClosingCompanyFields && (
        <ClosingCompanySelector
          companies={closingCompanySelectorOptions}
          disabled={isDisabled}
          selectedCompanyId={selectedClosingCompanyPrefix}
          onSelect={onSelectClosingCompany}
        />
      )}

      {!_.isNil(formState?.[CLOSING_FORM_PATH]) && (
        <ClosingDateCascade
          businessDays={closingCascadeProps.businessDays}
          closingFormValues={formState[CLOSING_FORM_PATH]}
          hasInterestCredit={closingCascadeProps.hasInterestCredit}
          isChattel={closingCascadeProps.isChattel}
          loanApplication={closingCascadeProps.loanApplication}
          maturityTermMonths={closingCascadeProps.maturityTermMonths}
          setCurrentCloseAt={closingCascadeProps.setCurrentCloseAt}
          setCurrentFundingDate={closingCascadeProps.setCurrentFundingDate}
          setCurrentNoteDate={closingCascadeProps.setCurrentNoteDate}
          setInForm={setInForm}
          subjectPropertyOccupancy={closingCascadeProps.subjectPropertyOccupancy}
          subjectPropertyState={closingCascadeProps.subjectPropertyState}
        />
      )}

      {sectionResolutions.map(
        ({ section, kind, iframeConfig, loanFieldGroups, customFieldResolutions }) =>
          kind === LoanEditorCustomFormSectionKind.IFRAME ? (
            <IframeSection
              iframeHeightPx={iframeConfig.iframeHeightPx}
              iframeUrl={iframeConfig.iframeUrl}
              key={section.id ?? section.title}
              title={section.title}
              tokenContext={iframeTokenContext}
            />
          ) : (
            <DrawerFieldsSection
              customFieldResolutions={customFieldResolutions}
              customFieldsController={customFieldsController}
              disabled={isDisabled}
              formState={formState}
              handleChange={handleChange}
              key={section.id ?? section.title}
              loanFieldGroups={loanFieldGroups}
              mergedPendingChanges={mergedPendingChanges}
              section={section}
              setInForm={setInForm}
              staticValidateField={staticValidateField}
              validateField={validateField}
              validationErrors={validationErrors}
            />
          ),
      )}
    </Box>
  );
};

DrawerContent.propTypes = {
  contentRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  sectionResolutions: PropTypes.arrayOf(PropTypes.object).isRequired,
  unsupportedEntries: PropTypes.arrayOf(PropTypes.object).isRequired,
  inapplicableEntries: PropTypes.arrayOf(PropTypes.object).isRequired,
  hasBorrowerFields: PropTypes.bool.isRequired,
  borrowerSelectorOptions: PropTypes.arrayOf(EntityOptionPropType).isRequired,
  hasContactFields: PropTypes.bool.isRequired,
  contactSelectorOptions: PropTypes.arrayOf(EntityOptionPropType).isRequired,
  hasClosingCompanyFields: PropTypes.bool.isRequired,
  closingCompanySelectorOptions: PropTypes.arrayOf(EntityOptionPropType).isRequired,
  closingCascadeProps: PropTypes.object.isRequired,
  iframeTokenContext: PropTypes.object.isRequired,
  customFieldsController: PropTypes.object.isRequired,
  formState: PropTypes.object.isRequired,
  mergedPendingChanges: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  setInForm: PropTypes.func.isRequired,
  staticValidateField: PropTypes.func.isRequired,
  validateField: PropTypes.func.isRequired,
  onSelectBorrower: PropTypes.func.isRequired,
  onSelectContact: PropTypes.func.isRequired,
  onSelectClosingCompany: PropTypes.func.isRequired,
  form: PropTypes.shape({
    description: PropTypes.string,
    sections: PropTypes.array,
  }),
  selectedBorrowerId: PropTypes.string,
  selectedContactId: PropTypes.string,
  selectedClosingCompanyPrefix: PropTypes.string,
  validationErrors: PropTypes.object,
};

export default memo(DrawerContent);
